import {UIGameCardContainerView} from './UIGameCardContainerView'
import {CardView} from '../../ui_canvas_engine/CardView'
import {ArrayUtils} from '@colonist/utils'
import {SectionBgView} from '../../ui_canvas_engine/SectionBgView'
import {TextView} from '../../ui_canvas_engine/TextView'
import {TranslationObject} from '@colonist/i18n'
import {GameColor} from '../../GameColor'
import {CardDataController} from '@colonist/game-card-types'
import {TradeUtils} from '@colonist/game-mechanic-validators'
import {CardEnum} from '@colonist/game-states'

export class UIGameCardWithRatioContainerView extends UIGameCardContainerView {
    private readonly cardRatioViews: CardRatioView[] = []
    private isCardRatiosVisible: boolean = false
    private isCardRatiosFadeEnabled = true

    protected addCardView(cardView: CardView, animate?: boolean): void {
        super.addCardView(cardView, animate)

        const isDevCard = !CardDataController.isResourceCard(cardView.cardData.cardEnum)
        if(isDevCard) return

        this.createCardRatioView(cardView)
    }

    setRatiosVisible(visible: boolean) {
        this.isCardRatiosVisible = visible
        this.cardRatioViews.forEach((cardRatioView) => {
            this.updateCardFade(cardRatioView.cardEnum)
            cardRatioView.visible = visible
        })
    }

    setRatioFadeEnabled(enabled: boolean) {
        this.isCardRatiosFadeEnabled = enabled
    }

    removeCard(cardEnum: CardEnum): void {
        super.removeCard(cardEnum)

        this.destroyCardRatioView(cardEnum)

        const topCardView = this.cardInventory.getTopCardWithType(cardEnum)
        if(topCardView) this.createCardRatioView(topCardView)
    }

    updateAllCardRatios() {
        this.cardRatioViews.forEach((cardRatioView) => {
            const bankTradeRatios = this.uiGameManager.gameController.getPlayerBankTradeRatios(this.uiGameManager.gameController.myColor)
            const ratio = TradeUtils.getBankTradeRatioForCard(bankTradeRatios, cardRatioView.cardEnum)
            cardRatioView.setRatioText(ratio)
            this.updateCardFade(cardRatioView.cardEnum)
        })
    }

    private createCardRatioView(cardView: CardView) {
        this.destroyCardRatioView(cardView.cardData.cardEnum)

        const bankTradeRatios = this.uiGameManager.gameController.getPlayerBankTradeRatios(this.uiGameManager.gameController.myColor)
        const cardRatio = TradeUtils.getBankTradeRatioForCard(bankTradeRatios, cardView.cardData.cardEnum)
        const cardRatioView = new CardRatioView(this.cardW, this.cardH, cardView, cardRatio)
        cardRatioView.visible = this.isCardRatiosVisible
        this.cardRatioViews.push(cardRatioView)
        this.updateCardFade(cardView.cardData.cardEnum)
    }

    private destroyCardRatioView(cardEnum: CardEnum) {
        const existingCardRatioView = this.getCardRatioView(cardEnum)
        if(existingCardRatioView === undefined) return

        ArrayUtils.removeElementFromArray(this.cardRatioViews, existingCardRatioView)
        existingCardRatioView.destroy()
    }

    private updateCardFade(cardEnum: CardEnum) {
        if(!this.isCardRatiosFadeEnabled) return

        const bankTradeRatios = this.uiGameManager.gameController.getPlayerBankTradeRatios(this.uiGameManager.gameController.myColor)
        const requiredCardAmount = TradeUtils.getBankTradeRatioForCard(bankTradeRatios, cardEnum)
        const currentCardAmount = this.cardInventory.getCardTypeCount(cardEnum)
        const canAffordRatio = currentCardAmount >= requiredCardAmount
        const fadeOutCard = !canAffordRatio && this.isCardRatiosVisible
        const cardViews = this.cardInventory.getAllCardViewsOfType(cardEnum)

        if(fadeOutCard) cardViews.forEach((card) => card.fadeOutCard())
        else cardViews.forEach((card) => card.fadeInCard())
    }

    private getCardRatioView(cardEnum: CardEnum): CardRatioView | undefined {
        return this.cardRatioViews.find((cardRatioView) => cardRatioView.cardEnum === cardEnum)
    }
}

class CardRatioView extends SectionBgView {
    readonly cardEnum: CardEnum
    private readonly ratioText: TextView

    constructor(cardW: number, cardH: number, cardView: CardView, ratio: number) {
        const cardRatioTextSize = cardH * 0.25
        const cardBorderSize = cardW * 0.05
        const width = cardW - cardBorderSize * 2
        super(cardBorderSize, cardH - cardRatioTextSize - cardBorderSize, width, cardRatioTextSize, cardView, true)

        const bgAlpha = 0.8
        const color = GameColor.getColorHexValue(GameColor.Color.White)
        this.drawBg(color, bgAlpha, undefined, undefined, undefined, 0)
        this.cardEnum = cardView.cardData.cardEnum
        const text = CardRatioView.getRatioText(ratio)
        this.ratioText = new TextView(text, 0, 0, width, cardRatioTextSize, this)
        this.ratioText.centerX()
    }

    setRatioText(ratio: number) {
        const text = CardRatioView.getRatioText(ratio)
        this.ratioText.setText(text)
        this.ratioText.centerX()
    }

    private static getRatioText(ratio: number): TranslationObject {
        return {key: 'strings:colonistCommon.utils.#buffer', options: {value: `${ratio}:1`}}
    }
}
